import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core'
import { Router, RouteReuseStrategy } from '@angular/router'
import { TranslocoService } from '@jsverse/transloco'
import { firstValueFrom } from 'rxjs'
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import * as Sentry from '@sentry/angular'
import { TranslocoRootModule } from './transloco-root.module'
import { L10n, setCulture } from '@syncfusion/ej2-base'
import { IonicModule, IonicRouteStrategy } from '@ionic/angular'
import { AppRoutingModule } from './app-routing.module'
import { FeathersService } from './services/feathers.service'
import { AuthGuardService } from './services/guards/auth-guard.service'
import { NonAuthGuardService } from './services/guards/non-auth-guard.service'
import { DebugService } from './services/debug/debug.service'
import { registerLocaleData } from '@angular/common'
import localeCs from '@angular/common/locales/cs'
import { ActiveModuleGuardService } from './services/guards/active-module-guard.service'
import { provideServiceWorker } from '@angular/service-worker'
import { environment } from '../environments/environment'
import { ConnectionAlertService } from './services/connectionAlert/connection-alert.service'
import { GlobalErrorHandlerService } from './services/exception/global-error-handler.service'
import { ExceptionHelperService } from './services/exception/exception-helper.service'
import { NgxsModule } from '@ngxs/store'
import { APP_STATES } from './store'
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin'
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin'
import { providePrimeNG } from 'primeng/config'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { PrimeNgCustomPreset } from './primeNgSettings/prime-ng-custom-preset'
import primeNgTranslations from './primeNgSettings/prime-ng-cs.json'
import { ConfirmationService, MessageService } from 'primeng/api'

registerLocaleData(localeCs)

// Initialize Syncfusion locale
setCulture('cs-CZ')
// Load locale from assets
if (typeof fetch !== 'undefined') {
  fetch('/assets/locale.json')
    .then((response) => response.json())
    .then((locale) => {
      L10n.load(locale)
    })
    .catch(() => {
      // Silently fail if locale.json is not available
    })
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Restores Zone.js-based CD scheduler (onMicrotaskEmpty) so async ngOnChanges completes fully before CD runs, preventing NG0100
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      IonicModule.forRoot({
        animated: false,
        navAnimation: null,
        innerHTMLTemplatesEnabled: true,
        mode: 'md',
      }),
      AppRoutingModule,
      TranslocoRootModule,
      NgxsModule.forRoot(APP_STATES),
      NgxsLoggerPluginModule.forRoot(),
      NgxsReduxDevtoolsPluginModule.forRoot(),
    ),
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService)
      transloco.setDefaultLang('cs')
      transloco.setActiveLang('cs')
      return firstValueFrom(transloco.load('cs'))
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    MessageService,
    ConfirmationService,
    FeathersService,
    ConnectionAlertService,
    AuthGuardService,
    NonAuthGuardService,
    ActiveModuleGuardService,
    DebugService,
    { provide: LOCALE_ID, useValue: 'cs' },
    ExceptionHelperService,
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: PrimeNgCustomPreset,
        options: {
          prefix: 'prime',
          darkModeSelector: false, // disable darkmode option
        },
      },
      translation: primeNgTranslations.cs,
    }),
  ],
}
